import { Routes } from '@angular/router';
import { ShellComponent } from './core/layout/shell.component';
import { authGuard } from './core/guards/auth.guard';
import { ownerGuard } from './core/guards/owner.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./features/auth/login.component').then((m) => m.LoginComponent),
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      // Staff users get exactly three screens — Add Order, Pending Orders and Orders
      // History (plus the invoice view those screens open). Every other route is
      // owner-only; ownerGuard sends a staff user to /orders/add instead.
      {
        path: 'dashboard',
        canActivate: [ownerGuard],
        loadComponent: () => import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'orders/add',
        loadComponent: () => import('./features/add-order/add-order.component').then((m) => m.AddOrderComponent),
      },
      {
        path: 'orders/pending',
        loadComponent: () =>
          import('./features/pending-orders/pending-orders.component').then((m) => m.PendingOrdersComponent),
      },
      {
        path: 'orders/history',
        loadComponent: () =>
          import('./features/order-history/order-history.component').then((m) => m.OrderHistoryComponent),
      },
      {
        path: 'orders/quotations',
        canActivate: [ownerGuard],
        loadComponent: () =>
          import('./features/quotations/quotations.component').then((m) => m.QuotationsComponent),
      },
      {
        path: 'orders/:orderId/invoice',
        loadComponent: () =>
          import('./features/generate-bill/generate-bill.component').then((m) => m.GenerateBillComponent),
      },
      {
        path: 'settings/categories',
        canActivate: [ownerGuard],
        loadComponent: () => import('./features/categories/categories.component').then((m) => m.CategoriesComponent),
      },
      {
        path: 'settings/products',
        canActivate: [ownerGuard],
        loadComponent: () => import('./features/products/products.component').then((m) => m.ProductsComponent),
      },
      {
        path: 'settings/invoice',
        canActivate: [ownerGuard],
        loadComponent: () =>
          import('./features/invoice-settings/invoice-settings.component').then((m) => m.InvoiceSettingsComponent),
      },
      {
        path: 'settings/staff',
        canActivate: [ownerGuard],
        loadComponent: () =>
          import('./features/staff-users/staff-users.component').then((m) => m.StaffUsersComponent),
      },
      // ── Expenses ───────────────────────────────────────────────────────
      // Two screens only: record an expense, and post to the supplier/accounts ledger.
      // The expense DASHBOARD is not a route of its own — it is a view of /dashboard,
      // picked from the switcher there, so there is one place to look at numbers.
      //
      // Owner-only. The guard is on the ROUTE, not just the nav item, so a staff
      // member following a bookmarked URL is bounced rather than landing on a screen
      // that renders empty and then fills with permission errors. The real boundary is
      // still server-side: every expense endpoint calls requireOwner(), and the
      // collections behind them are NO_ACCESS to the generic schema route.
      {
        path: 'expenses',
        canActivate: [ownerGuard],
        loadComponent: () => import('./features/expenses/expenses.component').then((m) => m.ExpensesComponent),
      },
      {
        path: 'expenses/accounts',
        canActivate: [ownerGuard],
        loadComponent: () =>
          import('./features/supplier-accounts/supplier-accounts.component').then((m) => m.SupplierAccountsComponent),
      },

      {
        path: 'reports/account-statement',
        canActivate: [ownerGuard],
        loadComponent: () =>
          import('./features/account-statement/account-statement.component').then((m) => m.AccountStatementComponent),
      },
      {
        path: 'reports/account-summary',
        canActivate: [ownerGuard],
        loadComponent: () =>
          import('./features/reports/account-summary.component').then((m) => m.AccountSummaryComponent),
      },
      {
        path: 'reports/sales',
        canActivate: [ownerGuard],
        loadComponent: () => import('./features/reports/sales-report.component').then((m) => m.SalesReportComponent),
      },
      {
        path: 'reports/dues',
        canActivate: [ownerGuard],
        loadComponent: () => import('./features/reports/dues-report.component').then((m) => m.DuesReportComponent),
      },
      {
        path: 'reports/cash-collection',
        canActivate: [ownerGuard],
        loadComponent: () =>
          import('./features/reports/cash-collection-report.component').then((m) => m.CashCollectionReportComponent),
      },
      {
        path: 'reports/orders-by-user',
        canActivate: [ownerGuard],
        loadComponent: () =>
          import('./features/reports/orders-by-user-report.component').then((m) => m.OrdersByUserReportComponent),
      },
      // Catch-all INSIDE the shell so an unknown path keeps the nav chrome and the user
      // can navigate onwards, rather than dropping them on a bare page.
      {
        path: '**',
        loadComponent: () => import('./features/not-found/not-found.component').then((m) => m.NotFoundComponent),
      },
    ],
  },
  // Catch-all for unauthenticated users, who never reach the guarded shell above.
  { path: '**', redirectTo: '' },
];
